/**
 * Advanced Search Parser
 * 
 * Parses search queries with boolean operators (AND, OR, NOT) and field-targeted searches.
 * 
 * Supported syntax:
 * - AND: Space-separated terms (default) or explicit "AND"
 * - OR: Use "OR" (uppercase) between terms
 * - NOT: Use "NOT" (uppercase) or "-" (minus sign) before term
 * - Field targeting: field:value (e.g., title:assessment, organization:City of Vancouver)
 * - Parentheses: Group terms (e.g., (contaminated OR remediation) AND BC)
 * 
 * Examples:
 * - "contaminated AND sites AND BC"
 * - "Vancouver OR Victoria OR Surrey"
 * - "assessment NOT preliminary"
 * - "-consulting"
 * - "(contaminated OR remediation) AND (BC OR Ontario)"
 * - "title:assessment AND description:remediation"
 */

export interface ParsedSearchQuery {
  // Main search terms (for general search across title, description, etc.)
  generalTerms: string[];
  
  // Field-specific searches
  fieldSearches: Record<string, string[]>;
  
  // Boolean operators
  andTerms: string[];
  orTerms: string[];
  notTerms: string[];
  
  // Complex queries with parentheses
  hasComplexQuery: boolean;
  rawQuery: string;
}

/**
 * Available searchable fields from govt_opportunity entity
 */
export const SEARCHABLE_FIELDS = [
  'title',
  'description',
  'organization',
  'issuing_agency',
  'department',
  'location',
  'city',
  'state_province',
  'region',
  'country',
  'status',
  'opportunity_type',
  'keywords',
  'tags',
  'service_categories',
  'matching_service_lines',
  'naics_codes',
  'solicitation_number',
  'reference_number',
] as const;

export type SearchableField = typeof SEARCHABLE_FIELDS[number];

/**
 * Detect explicit boolean mode (AND/OR only) using standalone keywords.
 * Requirement: without explicit AND/OR, treat query as plain text.
 */
export function hasExplicitBooleanOperators(query: string): boolean {
  return /\b(?:AND|OR)\b/i.test(query || "");
}

/**
 * Normalize plain-text search input without injecting escape characters.
 * Parentheses, commas, and hyphens should remain literal user text.
 */
export function sanitizePlainTextSearch(query: string): string {
  return (query || "").replace(/\s+/g, " ").trim();
}

/**
 * Parse a search query into structured components
 */
export function parseSearchQuery(query: string): ParsedSearchQuery {
  if (!query || !query.trim()) {
    return {
      generalTerms: [],
      fieldSearches: {},
      andTerms: [],
      orTerms: [],
      notTerms: [],
      hasComplexQuery: false,
      rawQuery: '',
    };
  }

  const trimmed = query.trim();
  const result: ParsedSearchQuery = {
    generalTerms: [],
    fieldSearches: {},
    andTerms: [],
    orTerms: [],
    notTerms: [],
    hasComplexQuery: false,
    rawQuery: trimmed,
  };

  // Plain-text mode (no explicit AND/OR): treat entire query literally.
  // Parentheses, commas, and hyphens are not logical operators in this mode.
  if (!hasExplicitBooleanOperators(trimmed)) {
    const literal = sanitizePlainTextSearch(trimmed);
    result.andTerms = [literal];
    result.generalTerms = [literal];
    result.hasComplexQuery = false;
    return result;
  }

  // Boolean mode: parentheses can be logical grouping
  if (trimmed.includes('(') || trimmed.includes(')')) {
    result.hasComplexQuery = true;
    return result;
  }

  // Split by boolean operators (case-sensitive for AND, OR, NOT)
  // We need to preserve quoted strings and field:value patterns
  const tokens = tokenizeQuery(trimmed);
  
  let currentOperator: 'AND' | 'OR' | null = null;
  let inNotContext = false;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    
    // Check for NOT operator or minus sign
    if (token.toUpperCase() === 'NOT' || token === '-') {
      inNotContext = true;
      continue;
    }
    
    // Check for AND operator
    if (token.toUpperCase() === 'AND') {
      currentOperator = 'AND';
      continue;
    }
    
    // Check for OR operator
    if (token.toUpperCase() === 'OR') {
      currentOperator = 'OR';
      continue;
    }
    
    // Check for field:value pattern
    const fieldMatch = token.match(/^(\w+):(.+)$/);
    if (fieldMatch) {
      const [, field, value] = fieldMatch;
      const normalizedField = field.toLowerCase();
      
      // Validate field is searchable
      if (SEARCHABLE_FIELDS.includes(normalizedField as SearchableField)) {
        if (!result.fieldSearches[normalizedField]) {
          result.fieldSearches[normalizedField] = [];
        }
        result.fieldSearches[normalizedField].push(value.trim());
      } else {
        // Invalid field, treat as general term
        if (inNotContext) {
          result.notTerms.push(token);
        } else if (currentOperator === 'OR') {
          result.orTerms.push(token);
        } else {
          result.andTerms.push(token);
        }
      }
      inNotContext = false;
      currentOperator = null;
      continue;
    }
    
    // Regular term
    if (inNotContext) {
      result.notTerms.push(token);
      inNotContext = false;
    } else if (currentOperator === 'OR') {
      result.orTerms.push(token);
    } else {
      // Default to AND (space-separated terms are AND by default)
      result.andTerms.push(token);
    }
    currentOperator = null;
  }

  // Combine andTerms into generalTerms for backward compatibility
  result.generalTerms = [...result.andTerms];

  return result;
}

/**
 * Tokenize query while preserving quoted strings and field:value patterns
 */
function tokenizeQuery(query: string): string[] {
  const tokens: string[] = [];
  let currentToken = '';
  let inQuotes = false;
  let quoteChar = '';

  for (let i = 0; i < query.length; i++) {
    const char = query[i];
    const nextChar = query[i + 1];

    // Handle quotes
    if ((char === '"' || char === "'") && !inQuotes) {
      inQuotes = true;
      quoteChar = char;
      if (currentToken.trim()) {
        tokens.push(currentToken.trim());
        currentToken = '';
      }
      continue;
    }

    if (inQuotes && char === quoteChar) {
      inQuotes = false;
      if (currentToken.trim()) {
        tokens.push(currentToken.trim());
        currentToken = '';
      }
      continue;
    }

    if (inQuotes) {
      currentToken += char;
      continue;
    }

    // Handle whitespace
    if (/\s/.test(char)) {
      if (currentToken.trim()) {
        tokens.push(currentToken.trim());
        currentToken = '';
      }
      continue;
    }

    // Check for operators (must be whole words)
    const remaining = query.slice(i);
    const upperRemaining = remaining.toUpperCase();
    
    if (upperRemaining.startsWith(' AND ') || 
        (upperRemaining.startsWith('AND') && (i === 0 || /\s/.test(query[i - 1])))) {
      if (currentToken.trim()) {
        tokens.push(currentToken.trim());
        currentToken = '';
      }
      tokens.push('AND');
      i += 2; // Skip "AND"
      continue;
    }
    
    if (upperRemaining.startsWith(' OR ') || 
        (upperRemaining.startsWith('OR') && (i === 0 || /\s/.test(query[i - 1])))) {
      if (currentToken.trim()) {
        tokens.push(currentToken.trim());
        currentToken = '';
      }
      tokens.push('OR');
      i += 1; // Skip "OR"
      continue;
    }
    
    if (upperRemaining.startsWith(' NOT ') || 
        (upperRemaining.startsWith('NOT') && (i === 0 || /\s/.test(query[i - 1])))) {
      if (currentToken.trim()) {
        tokens.push(currentToken.trim());
        currentToken = '';
      }
      tokens.push('NOT');
      i += 2; // Skip "NOT"
      continue;
    }

    currentToken += char;
  }

  if (currentToken.trim()) {
    tokens.push(currentToken.trim());
  }

  return tokens;
}

/**
 * Convert parsed query to filter object for API
 * This handles the conversion to backend filter format
 * 
 * Note: For complex queries with OR/NOT, we use _search as a fallback
 * since the backend doesn't support advanced boolean operators yet.
 * Field-specific searches are applied as direct filters.
 */
export function parsedQueryToFilters(parsed: ParsedSearchQuery): Record<string, any> {
  const filters: Record<string, any> = {};

  // Field-specific searches (these work with direct field filters)
  Object.entries(parsed.fieldSearches).forEach(([field, values]) => {
    if (values.length === 1) {
      // Use _like for partial matching on field-specific searches
      filters[`${field}_like`] = `%${values[0]}%`;
    } else {
      // Multiple values for same field - use OR logic via _in if backend supports it
      // Otherwise, fall back to _search
      filters[`${field}_like`] = `%${values[0]}%`; // Use first value for now
    }
  });

  // Handle OR queries - preserve OR operator in search string
  if (parsed.orTerms.length > 0 && parsed.andTerms.length === 0 && !parsed.hasComplexQuery) {
    // Pure OR query - preserve OR operators in the search string
    const orQuery = parsed.orTerms.join(' OR ');
    filters._search = orQuery;
    
    // Store NOT terms for client-side filtering if needed
    if (parsed.notTerms.length > 0) {
      filters._exclude_terms = parsed.notTerms;
    }
    
    return filters;
  }
  
  // Handle complex queries (with AND+OR, NOT, or parentheses)
  if (parsed.hasComplexQuery || (parsed.orTerms.length > 0 && parsed.andTerms.length > 0) || parsed.notTerms.length > 0) {
    // For complex queries, preserve operators in search string
    const searchParts: string[] = [];
    
    if (parsed.andTerms.length > 0) {
      searchParts.push(parsed.andTerms.join(' AND '));
    }
    
    if (parsed.orTerms.length > 0) {
      if (searchParts.length > 0) {
        searchParts.push('AND');
      }
      searchParts.push('(' + parsed.orTerms.join(' OR ') + ')');
    }
    
    if (searchParts.length > 0) {
      filters._search = searchParts.join(' ');
    }
    
    // Store NOT terms for client-side filtering if needed
    if (parsed.notTerms.length > 0) {
      filters._exclude_terms = parsed.notTerms;
    }
    
    return filters;
  }

  // Simple AND query - use existing _search filter
  if (parsed.andTerms.length > 0) {
    filters._search = parsed.andTerms.join(' ');
  }

  return filters;
}

/**
 * Validate search query syntax
 */
export function validateSearchQuery(query: string): { valid: boolean; error?: string } {
  if (!query || !query.trim()) {
    return { valid: true };
  }

  // In plain-text mode, parentheses are literal characters and should not trigger syntax errors.
  if (!hasExplicitBooleanOperators(query)) {
    return { valid: true };
  }

  // Check for balanced parentheses
  let openCount = 0;
  for (const char of query) {
    if (char === '(') openCount++;
    if (char === ')') openCount--;
    if (openCount < 0) {
      return { valid: false, error: 'Unmatched closing parenthesis' };
    }
  }
  if (openCount > 0) {
    return { valid: false, error: 'Unmatched opening parenthesis' };
  }

  return { valid: true };
}

/**
 * Get search hints/suggestions based on current input
 */
export function getSearchHints(query: string): string[] {
  const hints: string[] = [];
  const lowerQuery = query.toLowerCase();

  // Field suggestions
  if (lowerQuery.includes(':')) {
    const fieldPart = query.split(':')[0].toLowerCase();
    const matchingFields = SEARCHABLE_FIELDS.filter(field => 
      field.startsWith(fieldPart) && field !== fieldPart
    );
    hints.push(...matchingFields.map(f => `${f}:`));
  } else {
    // Operator suggestions
    if (!lowerQuery.endsWith(' and') && !lowerQuery.endsWith(' or') && !lowerQuery.endsWith(' not')) {
      if (lowerQuery.includes(' ')) {
        hints.push('AND', 'OR');
      }
      if (lowerQuery.length > 0) {
        hints.push('NOT', '-');
      }
    }
  }

  return hints.slice(0, 5); // Limit to 5 hints
}
